import { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/common/PageHeader.jsx";
import "../../styles/studio/common/errors.css";
import {
  getErrorEvent,
  getErrorEvents,
  getErrorIssue,
  getErrorIssues,
  updateErrorEventStatus,
  updateErrorIssueStatus,
} from "../../services/error.service.js";

const STATUS_OPTIONS = ["open", "ignored", "resolved"];
const SOURCE_OPTIONS = ["all", "frontend", "backend"];

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const trimText = (value = "", maxLength = 100) => {
  if (typeof value !== "string") return "";
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const getEntityId = (activeTab, item) => {
  if (activeTab === "issues") return item.errorIssueId || item._id || "";
  return item.errorEventId || item._id || "";
};

const getRenderKey = (activeTab, item, index) => {
  const entityId = getEntityId(activeTab, item);
  if (entityId) return `${activeTab}-${entityId}-${index}`;
  return `${activeTab}-${item.fingerprint || "row"}-${item.createdAt || item.timestamp || index}-${index}`;
};

const Errors = () => {
  const [tab, setTab] = useState("issues");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("open");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [issuesList, setIssuesList] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
  });
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);

  const query = useMemo(
    () => ({
      page,
      limit,
      status,
      ...(source !== "all" ? { source } : {}),
      ...(search ? { search } : {}),
    }),
    [page, limit, status, source, search],
  );

  const list = tab === "issues" ? issuesList : eventsList;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setSelectedId("");
    setDetail(null);
    setPagination({
      page: 1,
      totalPages: 1,
      totalCount: 0,
    });
  }, [tab]);

  useEffect(() => {
    const loadList = async () => {
      setIsLoadingList(true);
      try {
        const result =
          tab === "issues" ? await getErrorIssues(query) : await getErrorEvents(query);
        if (tab === "issues") {
          setIssuesList(result.records || []);
        } else {
          setEventsList(result.records || []);
        }
        setPagination({
          page: result.pagination?.page || 1,
          totalPages: result.pagination?.totalPages || 1,
          totalCount: result.pagination?.totalCount || 0,
        });
      } catch (error) {
        if (tab === "issues") {
          setIssuesList([]);
        } else {
          setEventsList([]);
        }
      } finally {
        setIsLoadingList(false);
      }
    };
    loadList();
  }, [tab, query]);

  useEffect(() => {
    if (!selectedId) return;
    const loadDetail = async () => {
      setIsLoadingDetail(true);
      try {
        const result =
          tab === "issues"
            ? await getErrorIssue(selectedId)
            : await getErrorEvent(selectedId);
        setDetail(result || null);
      } catch (error) {
        setDetail(null);
      } finally {
        setIsLoadingDetail(false);
      }
    };
    loadDetail();
  }, [selectedId, tab]);

  const handleSelect = (id) => {
    setSelectedId(id);
  };

  const handleStatusUpdate = async (nextStatus) => {
    if (!selectedId || !STATUS_OPTIONS.includes(nextStatus)) return;
    setIsUpdatingStatus(true);
    try {
      if (tab === "issues") {
        const updated = await updateErrorIssueStatus(selectedId, nextStatus);
        setDetail(updated);
      } else {
        const updated = await updateErrorEventStatus(selectedId, nextStatus);
        setDetail(updated);
      }
      const refreshed =
        tab === "issues" ? await getErrorIssues(query) : await getErrorEvents(query);
      if (tab === "issues") {
        setIssuesList(refreshed.records || []);
      } else {
        setEventsList(refreshed.records || []);
      }
      setPagination({
        page: refreshed.pagination?.page || 1,
        totalPages: refreshed.pagination?.totalPages || 1,
        totalCount: refreshed.pagination?.totalCount || 0,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <PageHeader
        title={"Error Monitoring"}
        description={
          "Track reported frontend/backend failures, inspect full context, and resolve issues quickly."
        }
      />

      <section className="errors-page">
        <div className="errors-tabs">
          <button
            className={`errors-tab ${tab === "issues" ? "active" : ""}`}
            onClick={() => setTab("issues")}
          >
            Issues
          </button>
          <button
            className={`errors-tab ${tab === "events" ? "active" : ""}`}
            onClick={() => setTab("events")}
          >
            Events
          </button>
        </div>

        <div className="errors-controls">
          <input
            className="errors-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={`Search ${tab}...`}
          />
          <div className="errors-selects">
            <select
              className="errors-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              className="errors-select"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setPage(1);
              }}
            >
              {SOURCE_OPTIONS.map((option) => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="errors-overview">
          <span>{pagination.totalCount} records</span>
          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>
        </div>

        <div className="errors-grid">
          <div className="errors-list-card">
            {isLoadingList ? (
              <p className="errors-state">Loading...</p>
            ) : list.length === 0 ? (
              <p className="errors-state">No records found.</p>
            ) : (
              <ul className="errors-list">
                {list.map((item, index) => {
                  const id = getEntityId(tab, item);
                  const renderKey = getRenderKey(tab, item, index);
                  return (
                    <li
                      key={renderKey}
                      className={`errors-item ${selectedId === id ? "selected" : ""}`}
                      onClick={() => id && handleSelect(id)}
                    >
                      <div className="errors-item-head">
                        <span className={`pill ${item.status || "open"}`}>
                          {item.status || "open"}
                        </span>
                        <span className="pill source">{item.source || "-"}</span>
                        {tab === "issues" && <span className="pill">{item.count || 0}</span>}
                      </div>
                      <h3>{trimText(item.lastName || item.name || "Error", 55)}</h3>
                      <p>{trimText(item.lastMessage || item.message || "-", 110)}</p>
                      <div className="errors-item-foot">
                        <span>{trimText(item.lastRoute || item.route || "-", 40)}</span>
                        <span>{formatDate(item.lastSeen || item.timestamp || item.createdAt)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="errors-pagination">
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setPage((prev) =>
                    Math.min(pagination.totalPages || 1, prev + 1),
                  )
                }
                disabled={page >= (pagination.totalPages || 1)}
              >
                Next
              </button>
            </div>
          </div>

          <div className="errors-detail-card">
            {!selectedId ? (
              <p className="errors-state">Select a record to view full details.</p>
            ) : isLoadingDetail ? (
              <p className="errors-state">Loading details...</p>
            ) : !detail ? (
              <p className="errors-state">Unable to load details.</p>
            ) : (
              <>
                <div className="errors-detail-head">
                  <h2>{trimText(detail.lastName || detail.name || "Error detail", 80)}</h2>
                  <div className="errors-detail-actions">
                    {STATUS_OPTIONS.map((option) => (
                      <button
                        key={option}
                        className={`status-btn ${option}`}
                        disabled={isUpdatingStatus}
                        onClick={() => handleStatusUpdate(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="kv-grid">
                  <div>
                    <label>Status</label>
                    <p>{detail.status || "-"}</p>
                  </div>
                  <div>
                    <label>Source</label>
                    <p>{detail.source || "-"}</p>
                  </div>
                  <div>
                    <label>Fingerprint</label>
                    <p className="mono">{detail.fingerprint || "-"}</p>
                  </div>
                  <div>
                    <label>Request ID</label>
                    <p className="mono">{detail.lastRequestId || detail.requestId || "-"}</p>
                  </div>
                  <div>
                    <label>Route</label>
                    <p className="mono">{detail.lastRoute || detail.route || "-"}</p>
                  </div>
                  <div>
                    <label>Method</label>
                    <p>{detail.lastMethod || detail.method || "-"}</p>
                  </div>
                  <div>
                    <label>Status Code</label>
                    <p>{detail.lastStatusCode || detail.statusCode || "-"}</p>
                  </div>
                  <div>
                    <label>Count</label>
                    <p>{detail.count || "-"}</p>
                  </div>
                  <div>
                    <label>First Seen</label>
                    <p>{formatDate(detail.firstSeen)}</p>
                  </div>
                  <div>
                    <label>Last Seen</label>
                    <p>{formatDate(detail.lastSeen || detail.timestamp)}</p>
                  </div>
                </div>

                {detail.lastMessage || detail.message ? (
                  <div className="errors-block">
                    <h3>Message</h3>
                    <pre>{detail.lastMessage || detail.message}</pre>
                  </div>
                ) : null}

                {detail.stack || detail.latestEventId?.stack ? (
                  <div className="errors-block">
                    <h3>Stack</h3>
                    <pre>{detail.stack || detail.latestEventId?.stack}</pre>
                  </div>
                ) : null}

                {detail.metadata || detail.latestEventId?.metadata ? (
                  <div className="errors-block">
                    <h3>Metadata</h3>
                    <pre>
                      {JSON.stringify(
                        detail.metadata || detail.latestEventId?.metadata || {},
                        null,
                        2,
                      )}
                    </pre>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Errors;
